import { Download, Trash2 } from "lucide-react";
import { useState } from "react";
import { Alert, Button, Card, Modal } from "@/components/common";
import { PasswordInput } from "@/components/common/PasswordInput";
import { useDeleteAccount, useDownloadDataExport } from "@/hooks/useSettings";

export function PrivacyTab() {
  return (
    <div className="space-y-6">
      <DataExportSection />
      <DeleteAccountSection />
    </div>
  );
}

function DataExportSection() {
  const downloadExport = useDownloadDataExport();
  const [success, setSuccess] = useState(false);

  return (
    <Card>
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-adara-50 p-2 text-adara-700 dark:bg-adara-900/30 dark:text-adara-300">
          <Download size={20} aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-main">Eksport danych</h3>
          <p className="mt-1 text-xs text-dim">
            Pobierz kopię wszystkich swoich danych w formacie JSON
          </p>
        </div>
      </div>

      {success && (
        <Alert
          variant="success"
          className="mt-4"
          onDismiss={() => {
            setSuccess(false);
          }}
        >
          Twoje dane zostały pobrane.
        </Alert>
      )}

      {downloadExport.isError && (
        <Alert variant="error" className="mt-4">
          Nie udało się pobrać danych. Spróbuj ponownie.
        </Alert>
      )}

      <div className="mt-4">
        <Button
          variant="secondary"
          leftIcon={<Download size={16} aria-hidden="true" />}
          isLoading={downloadExport.isPending}
          onClick={() => {
            downloadExport.mutate(undefined, {
              onSuccess: () => {
                setSuccess(true);
              },
            });
          }}
        >
          Pobierz dane
        </Button>
      </div>
    </Card>
  );
}

function DeleteAccountSection() {
  const deleteAccount = useDeleteAccount();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [password, setPassword] = useState("");

  function handleDelete(e: React.FormEvent) {
    e.preventDefault();
    deleteAccount.mutate(password);
  }

  function closeModal() {
    setIsModalOpen(false);
    setPassword("");
  }

  return (
    <Card>
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-red-50 p-2 text-red-600 dark:bg-red-900/30 dark:text-red-400">
          <Trash2 size={20} aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-main">Usunięcie konta</h3>
          <p className="mt-1 text-xs text-dim">
            Ta operacja jest nieodwracalna. Wszystkie Twoje dane zostaną trwale
            usunięte.
          </p>
        </div>
      </div>

      <div className="mt-4">
        <Button
          variant="danger"
          leftIcon={<Trash2 size={16} aria-hidden="true" />}
          onClick={() => {
            setIsModalOpen(true);
          }}
        >
          Usuń konto
        </Button>
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal} title="Usunięcie konta">
        <Alert variant="warning" className="mb-4">
          Ta operacja jest <strong>nieodwracalna</strong>. Wszystkie Twoje dane,
          polisy i historia rozliczeń zostaną trwale usunięte.
        </Alert>

        {deleteAccount.isError && (
          <Alert variant="error" className="mb-4">
            {(deleteAccount.error as { message?: string })?.message ??
              "Nie udało się usunąć konta"}
          </Alert>
        )}

        <form onSubmit={handleDelete}>
          <p className="mb-3 text-sm text-sub">
            Aby potwierdzić, wpisz swoje hasło:
          </p>

          <PasswordInput
            label="Hasło"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            required
          />

          <div className="mt-5 flex justify-end gap-3">
            <Button variant="outline" onClick={closeModal}>
              Anuluj
            </Button>
            <Button
              type="submit"
              variant="danger"
              isLoading={deleteAccount.isPending}
              disabled={!password}
            >
              Trwale usuń konto
            </Button>
          </div>
        </form>
      </Modal>
    </Card>
  );
}
